import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconMapPinFilled } from '@tabler/icons-react';
import { replaceUrl } from 'components/helpers/history';
import { z } from 'zod';

import { TOUR_SHEET_COPY } from './tourBookingSheetCopy';
import { toBookingInput, zonedParts } from './TourBookingSteps';
import {
  bookTourFailureCopy,
  type ChosenSlot,
  ConfirmationStep,
  ContactSupportStep,
  LoginStep,
  PhoneNumberStep,
  ReviewStep,
  SlotPickerStep,
} from './TourBookingSteps';
import { useBookTour } from './useBookTour';
import { useSavePhoneNumber } from './useSavePhoneNumber';
import { useTourAvailability } from './useTourAvailability';
import { useTourEligibility } from './useTourEligibility';

const RESTORED_SLOT_PARAMS = ['tourDate', 'tourTime', 'tourDuration'] as const;

// The PDP mounts several sheets at once (footer, main, lightbox call sites);
// the login-return replay must open exactly one of them.
let replayConsumed = false;

/** Resets the page-level replay guard; test isolation only. */
export const resetTourBookingReplayForTests = (): void => {
  replayConsumed = false;
};

const restoredSlotSchema = z.object({
  tourDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((value) => !Number.isNaN(new Date(`${value}T12:00:00`).getTime()), {
      message: 'not a calendar date',
    }),
  tourTime: z
    .string()
    .regex(/^T(\d{2}):(\d{2}):(\d{2})$/)
    .refine(
      (value) => {
        const hour = Number(value.slice(1, 3));
        const minute = Number(value.slice(4, 6));
        const second = Number(value.slice(7, 9));
        return hour < 24 && minute < 60 && second < 60;
      },
      { message: 'not a wall-clock time' },
    ),
  tourDuration: z.coerce.number().int().min(15).max(120).default(30),
});

const restoredSlotFromUrl = (): ChosenSlot | null => {
  const parsed = restoredSlotSchema.safeParse({
    tourDate: new URLSearchParams(window.location.search).get('tourDate') ?? undefined,
    tourTime: new URLSearchParams(window.location.search).get('tourTime') ?? undefined,
    tourDuration: new URLSearchParams(window.location.search).get('tourDuration') ?? undefined,
  });
  if (!parsed.success) {
    return null;
  }
  const { tourDate, tourTime, tourDuration } = parsed.data;
  const hours = Number(tourTime.slice(1, 3));
  const minutes = tourTime.slice(4, 6);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return {
    appointmentDate: tourDate,
    appointmentTime: tourTime,
    durationMinutes: tourDuration,
    label: `${display}:${minutes} ${suffix}`,
    fromReplay: true,
  };
};

const stripRestoredSlotParams = (): void => {
  const url = new URL(window.location.href);
  let changed = false;
  for (const name of RESTORED_SLOT_PARAMS) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      changed = true;
    }
  }
  if (changed) {
    replaceUrl(url.pathname + url.search);
  }
};

export type TourBookingSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called when a login-return URL replay wants the sheet opened. */
  onOpenRequest?: () => void;
  listingId: string | null;
  /** Address line shown under the sheet title. */
  addressLine?: string;
  /** Listing address token for app-handoff badge attribution, when known. */
  addressToken?: string | null;
};

/**
 * The PDP booking sheet. Web books only: slot picker (public) -> login -> phone
 * number when the record has none (no OTP - the app's phone step verifies it at
 * login, together with identity) -> review -> confirmation, plus the
 * contactSupport terminal for flagged accounts. The two-QR modal stays the
 * fallback; the call sites render one or the other based on the flag.
 */
export const TourBookingSheet = ({
  isOpen,
  onClose,
  onOpenRequest,
  listingId,
  addressLine,
  addressToken,
}: TourBookingSheetProps) => {
  const { authentication, recordMarketingSmsConsent } = useAuth();
  const isSignedIn = authentication.state === 'authenticated';
  // The session resolves client-side; treating the pending state as signed-out
  // would bounce a login-returning buyer straight back to /login.
  const authResolved = authentication.state !== 'authenticating';
  const [chosenSlot, setChosenSlot] = useState<ChosenSlot | null>(null);
  const [phoneSaveFailed, setPhoneSaveFailed] = useState(false);
  const [phoneCollectedDegraded, setPhoneCollectedDegraded] = useState(false);

  const availability = useTourAvailability(isOpen && listingId ? listingId : null);
  // Guests are routed to login before eligibility is consulted; querying
  // unauthenticated would fail and report to Sentry on the most common path.
  const eligibility = useTourEligibility({ enabled: isOpen && isSignedIn });
  const { book, isBooking, lastResult, reset: resetBookTour } = useBookTour();
  const { save: savePhoneNumber, isSaving: isSavingPhone } = useSavePhoneNumber();

  const availabilityData = availability.availability;

  // Derived, never stored: a failed booking with a stale slot (or a replayed
  // slot that no longer exists in availability) resolves to nothing, which
  // sends the buyer back to the picker; a replayed slot that still exists
  // adopts the picker's full label.
  const effectiveSlot = useMemo(() => {
    if (
      lastResult?.kind === 'failed' &&
      (lastResult.reason === 'slotUnavailable' || lastResult.reason === 'invalidInput')
    ) {
      return null;
    }
    if (chosenSlot?.fromReplay !== true) {
      return chosenSlot;
    }
    if (!availabilityData) {
      return chosenSlot;
    }
    const match = availabilityData.days
      .flatMap((day) => day.slots)
      .find((slot) => {
        const candidate = toBookingInput(slot, availabilityData.timezone);
        return (
          slot.enabled &&
          candidate.appointmentDate === chosenSlot.appointmentDate &&
          candidate.appointmentTime === chosenSlot.appointmentTime
        );
      });
    return match ? toBookingInput(match, availabilityData.timezone) : null;
  }, [availabilityData, chosenSlot, lastResult]);

  // A login return lands back on the PDP with the chosen slot in the URL
  // (D7): reopen the sheet at the post-login step and clean the URL.
  useEffect(() => {
    if (replayConsumed) {
      return;
    }
    replayConsumed = true;
    const restored = restoredSlotFromUrl();
    if (!restored) {
      return;
    }
    stripRestoredSlotParams();
    if (!isOpen) {
      // eslint-disable-next-line react/set-state-in-effect -- mount-time URL replay; must also fire the parent onOpenRequest side effect
      setChosenSlot(restored);
      onOpenRequest?.();
    }
  }, [isOpen, onOpenRequest]);

  // A closed sheet restarts the flow on reopen: the slot may no longer exist
  // (availability refetches), a booking failure must not trap the buyer in
  // review, and a completed booking must not replay its confirmation forever.
  const wasOpen = useRef(false);
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      setChosenSlot(null);
      setPhoneSaveFailed(false);
      setPhoneCollectedDegraded(false);
      resetBookTour();
    }
    wasOpen.current = isOpen;
  }, [isOpen, resetBookTour]);

  const { nextStep, refresh: refreshEligibility, isLoading: eligibilityLoading } = eligibility;
  const eligibilityFailed = eligibility.error != null;

  const handleSlotChosen = useCallback(
    (slot: ChosenSlot) => {
      setChosenSlot(slot);
      resetBookTour();
    },
    [resetBookTour],
  );

  const handleLoginContinue = useCallback((redirectTo: string) => {
    window.location.href = redirectTo;
  }, []);

  // Books exactly what the review step displays; on a login-return replay that
  // is the availability-reconciled slot, not the raw URL params.
  const handleConfirm = useCallback(() => {
    if (!listingId || !effectiveSlot) {
      return;
    }
    void book({
      listingId,
      appointmentDate: effectiveSlot.appointmentDate,
      appointmentTime: effectiveSlot.appointmentTime,
      durationMinutes: effectiveSlot.durationMinutes,
    });
  }, [book, effectiveSlot, listingId]);

  const handleSavePhone = useCallback(
    (phoneNumber: string, marketingOptIn: boolean, consentLanguage: string) => {
      void savePhoneNumber(phoneNumber).then((result) => {
        if (!isOpenRef.current) {
          return;
        }
        if (result.kind === 'failed') {
          setPhoneSaveFailed(true);
          return;
        }
        setPhoneSaveFailed(false);
        setPhoneCollectedDegraded(true);
        // Best-effort by contract and never throws; the number itself is already
        // saved, so a consent write failure only downgrades Iterable SMS.
        const email = authentication.user?.email;
        if (email) {
          void recordMarketingSmsConsent({ optIn: marketingOptIn, email, consentLanguage });
        }
        void refreshEligibility();
      });
    },
    [authentication.user, recordMarketingSmsConsent, refreshEligibility, savePhoneNumber],
  );

  const step = useMemo(() => {
    // Booked first: a refetch disables the just-booked slot, which would
    // otherwise null a replayed effectiveSlot and drop the confirmation.
    if (lastResult?.kind === 'booked') {
      return 'confirmation' as const;
    }
    if (!effectiveSlot) {
      return 'slot' as const;
    }
    if (!authResolved) {
      return 'eligibilityLoading' as const;
    }
    if (!isSignedIn) {
      return 'login' as const;
    }
    // While the fresh eligibility read is in flight nothing booking-relevant is
    // decided; the review step renders once the snapshot lands.
    if (eligibilityLoading) {
      return 'eligibilityLoading' as const;
    }
    if (eligibilityFailed) {
      // Degrade instead of dead-ending: the server re-checks access and phone at
      // booking, so a blind submit is safe; a blocked account gets the typed
      // blocked-account error there. The record could not be read, so also ask
      // for the number (covers OAuth accounts with none on file).
      return phoneCollectedDegraded ? ('review' as const) : ('phone' as const);
    }
    if (nextStep === 'contactSupport') {
      return 'contactSupport' as const;
    }
    if (nextStep === 'phone') {
      return 'phone' as const;
    }
    return 'review' as const;
  }, [
    authResolved,
    effectiveSlot,
    eligibilityFailed,
    eligibilityLoading,
    isSignedIn,
    lastResult,
    nextStep,
    phoneCollectedDegraded,
  ]);
  const bookedAppointment = lastResult?.kind === 'booked' ? lastResult.appointment : null;

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog.Root
      role="dialog"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog>
        <Dialog.Title className="sr-only">{TOUR_SHEET_COPY.sheetTitle}</Dialog.Title>
        <Col className="flex h-[640px] max-h-[85vh] w-full flex-col gap-4 md:w-165">
          {addressLine && (
            <Row className="items-center gap-1.5 px-4 md:px-5">
              <IconMapPinFilled size={16} color="var(--color-od-content-secondary)" />
              <Text as="span" className="text-body-sm text-od-content-secondary">
                {addressLine}
              </Text>
            </Row>
          )}
          <Col className="min-h-0 flex-1 gap-4 overflow-y-auto px-4 md:px-5">
            {availability.error && (
              <Col className="gap-3">
                <Text as="span" className="text-body-md text-od-content-secondary">
                  We couldn’t load tour times. Please try again.
                </Text>
                <Button
                  variant="secondary"
                  onClick={() => void availability.reload()}
                  analyticsName="tours-availability-retry"
                  aria-label="Retry loading tour times"
                >
                  Retry
                </Button>
              </Col>
            )}
            {!availability.error && step === 'slot' && (
              <SlotPickerStep
                days={availabilityData?.days ?? []}
                availableNow={availabilityData?.availableNow ?? false}
                nextAvailable={availabilityData?.nextAvailable ?? null}
                timezone={availabilityData?.timezone ?? null}
                onRetry={() => void availability.reload()}
                isLoading={availability.isLoading}
                onChoose={handleSlotChosen}
                notice={bookTourFailureCopy(lastResult)}
                onTourNow={() => {
                  if (availabilityData?.nextAvailable) {
                    // Tour-now books the next available slot; the same
                    // listing-timezone conversion as the grid applies.
                    const next = availabilityData.nextAvailable;
                    const parts = zonedParts(next.startTime, availabilityData?.timezone ?? null);
                    handleSlotChosen({
                      appointmentDate: parts.date,
                      appointmentTime: `T${String(parts.hour).padStart(2, '0')}:${parts.minute}:${parts.second}`,
                      durationMinutes: 30,
                      label: 'Now',
                    });
                  }
                }}
              />
            )}
            {!availability.error && step === 'login' && (
              <LoginStep slot={chosenSlot} onContinue={handleLoginContinue} />
            )}
            {!availability.error && !eligibility.error && step === 'eligibilityLoading' && (
              <Col className="h-full items-center justify-center">
                <Loader size="lg" aria-label="Loading" />
              </Col>
            )}
            {!availability.error && step === 'phone' && (
              <PhoneNumberStep
                isSaving={isSavingPhone}
                saveFailed={phoneSaveFailed}
                onSave={handleSavePhone}
              />
            )}
            {!availability.error && step === 'review' && effectiveSlot && (
              <ReviewStep
                slot={effectiveSlot}
                lastResult={lastResult}
                isBooking={isBooking}
                onConfirm={handleConfirm}
              />
            )}
            {!availability.error && step === 'confirmation' && bookedAppointment && (
              <ConfirmationStep appointment={bookedAppointment} addressToken={addressToken} />
            )}
            {!availability.error && !eligibility.error && step === 'contactSupport' && (
              <ContactSupportStep />
            )}
          </Col>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};
